import type { ZudokuConfig } from "zudoku";

const SITE_URL = "https://docs.providus.ai";

// Brand tokens copied from apps/docai-landing-web/app/globals.css so the docs
// and the marketing site cannot drift apart. No theme preset is used: a preset
// can only approximate the marketing site, and these are its actual values.
// radius 0 is deliberate — every surface on providus.ai is square.
//
// Zudoku uses shadcn semantics: `muted` is a SURFACE and `mutedForeground` is
// the dim text on it. The marketing site's text gray belongs in
// mutedForeground; putting it in `muted` painted light-gray panels into dark
// mode and made every inactive tab label invisible.
const dark = {
  background: "#0A0A0A",
  foreground: "#f0f4f8",
  card: "#111111",
  primary: "#6DBBFF",
  primaryForeground: "#111111",
  muted: "#1a1a1a",
  mutedForeground: "#A9A9A9",
  accent: "#1a2030",
  border: "rgba(255,255,255,0.1)",
  radius: "0rem",
};

const light = {
  background: "#ffffff",
  foreground: "#111111",
  card: "#f7f9fb",
  primary: "#2b6faa",
  primaryForeground: "#ffffff",
  muted: "#eef1f5",
  mutedForeground: "#5b6570",
  accent: "#eaf4ff",
  border: "rgba(0,0,0,0.1)",
  radius: "0rem",
};

// light theme takes the dark-ink logo, and the reverse.
const logoSrc = { light: "/logo-dark.png", dark: "/logo-light.png" };

const config: ZudokuConfig = {
  site: {
    title: "DocAI",
    logo: { src: logoSrc, alt: "DocAI by Providus", width: "116px" },
    showPoweredBy: false,
    footer: {
      logo: { src: logoSrc, alt: "Providus", width: "104px" },
      copyright: `© ${new Date().getFullYear()} Providus Technologies`,
      columns: [
        {
          title: "Documentation",
          links: [
            { label: "Quickstart", href: "/quickstart" },
            { label: "Parsing", href: "/parsing" },
            { label: "Extraction", href: "/extraction" },
            { label: "Knowledge bases", href: "/knowledge-bases" },
            { label: "API reference", href: "/api" },
            { label: "Error codes", href: "/errors" },
          ],
        },
        {
          title: "Agent tooling",
          links: [
            { label: "MCP server", href: "/mcp" },
            { label: "Editor plugins", href: "/plugins" },
            { label: "Agent guidance", href: "/skill" },
          ],
        },
        {
          title: "Providus",
          links: [
            { label: "providus.ai", href: "https://providus.ai" },
            { label: "Platform", href: "https://platform.providus.ai" },
          ],
        },
      ],
    },
  },

  // Top-level tabs rather than one long sidebar. The guides, the API reference
  // and the agent tooling are different jobs, and a reader is only ever doing
  // one of them.
  header: {
    navigation: [
      { label: "Guides", to: "/introduction" },
      {
        label: "Core APIs",
        items: [
          { label: "Parsing", to: "/parsing" },
          { label: "Extraction", to: "/extraction" },
          { label: "Split", to: "/split" },
          { label: "Classify", to: "/classify" },
          { label: "Knowledge bases and search", to: "/knowledge-bases" },
          { label: "Document workflows", to: "/workflows" },
        ],
      },
      {
        label: "Agent tooling",
        items: [
          { label: "MCP server", to: "/mcp" },
          { label: "Editor plugins", to: "/plugins" },
          { label: "Agent guidance", to: "/skill" },
        ],
      },
      { label: "API reference", to: "/api" },
      { label: "Error codes", to: "/errors" },
    ],
    themeSwitcher: { enabled: true },
  },

  theme: { dark, light },

  navigation: [
    {
      type: "category",
      label: "Get started",
      icon: "sparkles",
      items: [
        // Explicit label: the page title is the hero line, too long for a
        // sidebar entry.
        { type: "doc", file: "introduction", label: "Overview" },
        "/quickstart",
      ],
    },
    {
      type: "category",
      label: "Core APIs",
      icon: "layers",
      // Reading order, not alphabetical: you parse before you extract, and you
      // need a knowledge base before either is useful at scale.
      items: ["/parsing", "/extraction", "/split", "/classify", "/knowledge-bases", "/workflows"],
    },
    {
      type: "category",
      label: "Agent tooling",
      icon: "bot",
      items: ["/mcp", "/plugins", "/skill"],
    },
    {
      type: "category",
      label: "Reference",
      icon: "book",
      items: [
        {
          type: "link",
          to: "/api",
          label: "API reference",
          icon: "folder-cog",
        },
        "/errors",
      ],
    },
  ],

  // `/` is not a routed page: Zudoku only creates client routes for pages listed
  // in `navigation`, so a bare pages/index.mdx prerenders and then 404s on
  // hydrate.
  redirects: [{ from: "/", to: "/introduction" }],

  apis: [
    {
      type: "file",
      input: "./apis/docai-v1.json",
      path: "/api",
      // The docs make no live calls. A public page inviting a reader to paste
      // an API key is inviting an organization-root credential into a browser
      // and real credits onto someone's bill. Worked examples on the response
      // schemas show what comes back instead.
      disablePlayground: true,
    },
  ],

  // Absolute canonical on every prerendered page. Origin only, no trailing
  // slash — Meta.tsx joins it with basePath + pathname.
  canonicalUrlOrigin: SITE_URL,

  metadata: {
    // Without this every tab and SERP entry ended in "- Zudoku", the
    // framework default.
    title: "%s | DocAI docs",
    defaultTitle: "DocAI docs — on-premise document intelligence API",
    description:
      "Developer documentation for DocAI by Providus: parse, extract, knowledge bases, document workflows, the MCP server and the /v1 API reference.",
    // The default favicon was served from cdn.zudoku.dev.
    favicon: "/favicon.png",
    applicationName: "DocAI",
    publisher: "Providus Technologies",
    creator: "Providus Technologies",
  },

  // autoLastmod stamps build time, so every URL shared one value that changed
  // on each deploy. Google ignores an inaccurate lastmod, so none beats a wrong
  // one. Real per-page dates need a source outside the build (no git in image).
  sitemap: {
    siteUrl: SITE_URL,
    changefreq: "weekly",
    priority: 0.7,
    autoLastmod: false,
  },

  // llms.txt is cheap here because the .md files are already published. Google
  // ignores it and adoption is uneven, so this is upside, not a strategy.
  docs: { llms: { llmsTxt: true, llmsTxtFull: true } },

  search: { type: "pagefind" },
};

export default config;
